import { useEffect, useRef } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'default';
  isLoading?: boolean;
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  isLoading = false,
}: ConfirmDialogProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    confirmButtonRef.current?.focus();
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const confirmButtonClass =
    variant === 'danger'
      ? 'btn btn-primary bg-danger hover:bg-danger/90 shadow-none'
      : variant === 'warning'
        ? 'btn btn-primary bg-warning hover:bg-warning/90 shadow-none'
        : 'btn btn-primary shadow-glow';

  const icon = variant === 'danger' || variant === 'warning' ? AlertTriangle : HelpCircle;
  const iconStyles =
    variant === 'danger'
      ? 'text-danger bg-danger-bg border-danger/20'
      : variant === 'warning'
        ? 'text-warning bg-warning-bg border-warning/20'
        : 'text-primary bg-primary/10 border-primary/20';

  const Icon = icon;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !isLoading && onClose()}
      />

      {/* Dialog */}
      <div className="relative flex min-h-full items-end justify-center p-4 text-center sm:items-center">
        <div className="w-full max-w-lg rounded-card bg-background-card/90 backdrop-blur-xl border border-border shadow-glow overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className={`h-11 w-11 rounded-xl border flex items-center justify-center flex-shrink-0 ${iconStyles}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold text-text-header">{title}</h3>
                <p className="mt-2 text-sm text-text-muted">{message}</p>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-border bg-background-subtle/30 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              disabled={isLoading}
              onClick={onClose}
              className="btn btn-secondary"
            >
              {cancelText}
            </button>
            <button
              ref={confirmButtonRef}
              type="button"
              disabled={isLoading}
              onClick={onConfirm}
              className={confirmButtonClass}
            >
              {isLoading ? 'Processing…' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
